// Fotos y firmas (F9): se guardan comprimidas en el celular y se suben antes de la operación que las usa.
import { uuidv7 } from '@reparto/shared/nucleo';
import { api, ErrorApi, SinRed } from './api';
import { type ArchivoLocal, db } from './db';

const LADO_MAXIMO = 1600;

/** Reduce una foto a 1600 px de lado y JPEG 80 (documento 07). */
export async function comprimirFoto(archivo: Blob): Promise<{ blob: Blob; ancho: number; alto: number }> {
  const img = await createImageBitmap(archivo);
  const escala = Math.min(1, LADO_MAXIMO / Math.max(img.width, img.height));
  const ancho = Math.round(img.width * escala);
  const alto = Math.round(img.height * escala);
  const canvas = document.createElement('canvas');
  canvas.width = ancho;
  canvas.height = alto;
  canvas.getContext('2d')!.drawImage(img, 0, 0, ancho, alto);
  img.close();
  const blob = await new Promise<Blob>((ok, mal) =>
    canvas.toBlob((b) => (b ? ok(b) : mal(new Error('No se pudo comprimir'))), 'image/jpeg', 0.8),
  );
  return { blob, ancho, alto };
}

export async function guardarArchivo(
  a: Omit<ArchivoLocal, 'id' | 'estado' | 'intentos' | 'tomado_en'>,
): Promise<string> {
  const id = uuidv7();
  await db.archivos.put({ ...a, id, estado: 'local', intentos: 0, tomado_en: new Date().toISOString() });
  return id;
}

/** Sube un archivo local: URL prefirmada → PUT a MinIO → confirmar. Devuelve el id del archivo en el servidor. */
export async function subirArchivo(localId: string): Promise<string> {
  const a = await db.archivos.get(localId);
  if (!a) throw new Error(`Archivo local ${localId} inexistente`);
  if (a.estado === 'subido' && a.archivo_id) return a.archivo_id;
  try {
    const p = await api<{ archivo_id: string; url: string; cabeceras: Record<string, string> }>(
      '/archivos/url-subida',
      {
        cuerpo: { tipo: a.tipo, mime: a.mime, bytes: a.blob.size },
      },
    );
    let put: Response;
    try {
      put = await fetch(p.url, { method: 'PUT', body: a.blob, headers: p.cabeceras });
    } catch {
      throw new SinRed();
    }
    if (!put.ok) throw new ErrorApi(put.status, 'SUBIDA', 'El almacenamiento rechazó el archivo');
    await api(`/archivos/${p.archivo_id}/confirmar`, {
      cuerpo: { ancho: a.ancho, alto: a.alto, tomado_en: a.tomado_en },
    });
    await db.archivos.update(localId, { estado: 'subido', archivo_id: p.archivo_id });
    return p.archivo_id;
  } catch (e) {
    if (!(e instanceof SinRed))
      await db.archivos.update(localId, {
        intentos: a.intentos + 1,
        estado: a.intentos + 1 >= 3 ? 'fallido' : 'local',
      });
    throw e;
  }
}

/**
 * Archivos que no pudieron ir con su entrega: se suben después asociados a la parada (la API acepta `parada_id` al
 * pedir la URL). La entrega nunca queda bloqueada por una foto.
 */
export async function subirRezagados(): Promise<number> {
  const rezagados = await db.archivos.where('estado').equals('fallido').toArray();
  let subidos = 0;
  for (const a of rezagados) {
    try {
      const p = await api<{ archivo_id: string; url: string; cabeceras: Record<string, string> }>(
        '/archivos/url-subida',
        {
          cuerpo: { tipo: a.tipo, mime: a.mime, bytes: a.blob.size, parada_id: a.parada_id },
        },
      );
      const put = await fetch(p.url, { method: 'PUT', body: a.blob, headers: p.cabeceras });
      if (!put.ok) continue;
      await api(`/archivos/${p.archivo_id}/confirmar`, {
        cuerpo: { ancho: a.ancho, alto: a.alto, tomado_en: a.tomado_en },
      });
      await db.archivos.update(a.id, { estado: 'subido', archivo_id: p.archivo_id });
      subidos++;
    } catch {
      /* se reintenta en el próximo ciclo */
    }
  }
  return subidos;
}
